import { useMemo } from 'react';
import type { Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';
import type { WorkOrderBoardCardDto } from '@evimo/client/models';
import { WorkOrderStatus } from '@evimo/types/enums/maintenance-execution';
import type {
  KanbanColumnsMap,
  KanbanTicketView,
  KanbanWorkOrderView,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import {
  asOptionalString,
  buildPlanFilterOptions,
  KANBAN_COLUMN_STATUSES,
  sortKanbanWorkOrders,
  workOrderMatchesKanbanFilters,
  type KanbanColumnStatus,
  type KanbanSortOption,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { pickCurativeIncidentFields } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils/curative-incident-fields';

function mapTicket(
  ticket: WorkOrderBoardCardDto['tickets'][number],
): KanbanTicketView {
  const row = ticket as WorkOrderBoardCardDto['tickets'][number] & {
    taskId?: unknown;
    preventivePlanId?: unknown;
    planCode?: unknown;
    planName?: unknown;
    assetName?: unknown;
    assetSerialNumber?: unknown;
    lastExecutionResult?: unknown;
    incidentId?: unknown;
    incidentCode?: unknown;
    incidentTitle?: unknown;
    report?: { executionResult?: string | null } | null;
  };
  return {
    id: ticket.id,
    membershipId: ticket.membershipId,
    code: ticket.code,
    type: ticket.type,
    status: ticket.status,
    needsFollowUp: ticket.needsFollowUp,
    priority: ticket.priority,
    title: ticket.title,
    dueAt: ticket.dueAt ?? null,
    taskId: asOptionalString(row.taskId),
    preventivePlanId: asOptionalString(row.preventivePlanId),
    planCode: asOptionalString(row.planCode),
    planName: asOptionalString(row.planName),
    assetId: asOptionalString(ticket.assetId),
    assetName: asOptionalString(row.assetName),
    assetSerialNumber: asOptionalString(row.assetSerialNumber),
    lastExecutionResult:
      asOptionalString(row.lastExecutionResult) ??
      asOptionalString(row.report?.executionResult) ??
      null,
    ...pickCurativeIncidentFields(row),
  };
}

function highestPriority(tickets: KanbanTicketView[]): string {
  if (tickets.some((t) => t.priority === 'HIGH')) return 'HIGH';
  if (tickets.some((t) => t.priority === 'MEDIUM')) return 'MEDIUM';
  if (tickets.some((t) => t.priority === 'LOW')) return 'LOW';
  return 'MEDIUM';
}

function parseOptionalDate(
  value: string | Date | null | undefined,
): Date | null {
  if (value == null) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function mapBoardCardToWorkOrder(
  card: WorkOrderBoardCardDto,
  siteNameById: Map<string, string>,
  technicianNameById: Map<string, string>,
  t: TFunction<'maintenance'>,
): KanbanWorkOrderView {
  const tickets = card.tickets.map(mapTicket);
  const technicianId = asOptionalString(card.assignedTechnicianId);
  const createdAt = parseOptionalDate(card.createdAt) ?? new Date(0);
  const updatedAt = parseOptionalDate(card.updatedAt) ?? new Date(0);
  const completedAt = parseOptionalDate(
    (card as WorkOrderBoardCardDto & { completedAt?: string | Date | null })
      .completedAt,
  );
  const externalIdentifier = asOptionalString(
    (card as WorkOrderBoardCardDto & { externalIdentifier?: string | null })
      .externalIdentifier,
  );

  return {
    id: card.id,
    code: card.code,
    status: card.status as WorkOrderStatus,
    siteId: card.siteId,
    siteName:
      siteNameById.get(card.siteId) ?? t('workOrders.kanban.unknownSite'),
    externalIdentifier,
    assignedTechnicianId: technicianId,
    assignedTechnicianName: technicianId
      ? (technicianNameById.get(technicianId) ??
        t('workOrders.kanban.unknownTechnician'))
      : null,
    createdAt,
    updatedAt,
    completedAt,
    tickets,
    highestPriority: highestPriority(tickets),
  };
}

export type UseWorkOrderKanbanBoardDataArgs = {
  boardCards: WorkOrderBoardCardDto[] | undefined;
  siteNameById: Map<string, string>;
  technicianNameById: Map<string, string>;
  t: TFunction<'maintenance'>;
  siteFilter: string;
  technicianFilter: string;
  planFilter: string;
  followUpOnly: boolean;
  dueRange: [Dayjs | null, Dayjs | null] | null;
  kanbanSort: KanbanSortOption;
};

/**
 * Maps board DTOs into Kanban views and applies filters / column grouping / sort.
 */
export function useWorkOrderKanbanBoardData(
  args: UseWorkOrderKanbanBoardDataArgs,
) {
  const {
    boardCards,
    siteNameById,
    technicianNameById,
    t,
    siteFilter,
    technicianFilter,
    planFilter,
    followUpOnly,
    dueRange,
    kanbanSort,
  } = args;

  const workOrders = useMemo((): KanbanWorkOrderView[] => {
    return (boardCards ?? []).map((card) =>
      mapBoardCardToWorkOrder(card, siteNameById, technicianNameById, t),
    );
  }, [boardCards, siteNameById, technicianNameById, t]);

  const filteredWorkOrders = useMemo(() => {
    const dueFrom = dueRange?.[0] ?? null;
    const dueTo = dueRange?.[1] ?? null;
    return workOrders.filter((wo) =>
      workOrderMatchesKanbanFilters(wo, {
        siteId: siteFilter,
        technicianId: technicianFilter,
        planId: planFilter,
        followUpOnly,
        dueFrom,
        dueTo,
      }),
    );
  }, [
    workOrders,
    siteFilter,
    technicianFilter,
    planFilter,
    followUpOnly,
    dueRange,
  ]);

  const planFilterOptions = useMemo(
    () => buildPlanFilterOptions(workOrders),
    [workOrders],
  );

  const columnsMap = useMemo((): KanbanColumnsMap => {
    const map = Object.fromEntries(
      KANBAN_COLUMN_STATUSES.map((status) => [
        status,
        [] as KanbanWorkOrderView[],
      ]),
    ) as KanbanColumnsMap;

    for (const workOrder of filteredWorkOrders) {
      if (
        (KANBAN_COLUMN_STATUSES as readonly string[]).includes(workOrder.status)
      ) {
        map[workOrder.status as KanbanColumnStatus].push(workOrder);
      }
    }

    for (const status of KANBAN_COLUMN_STATUSES) {
      map[status] = sortKanbanWorkOrders(map[status], kanbanSort);
    }
    return map;
  }, [filteredWorkOrders, kanbanSort]);

  return {
    workOrders,
    filteredWorkOrders,
    columnsMap,
    planFilterOptions,
  };
}
